// The message on a card, read in place (module 10 U414; brief MESSAGE-AND-REPLY-ON-CARDS-BRIEF-2026-10-12, section A).
// One component, mounted on the item page's thread, the triage card and the Deals card's step box. The server cleans the
// text (apps/api/src/draft/clean-message.ts): quotes, signature and footer come folded away, and nothing is removed:
// "Show the signature and footer" and "Show the earlier mails" bring them back.
// BUGS.md T74 (module 10 U427, "Open the file itself"): an attachment's name opens the file, fetched from the mailbox it came
// to on the press; it no longer opens the text Cobrain read from it ("Show its text on the card" and "Both" were rejected).
// BUGS.md T73 (module 10 U426, "Folded until pressed"): on a card the message is its head only (from, to, date, subject,
// attachment count) until pressed; one press opens the cleaned message in place, whole. The item page's thread stays open.
// BUGS.md T75: on a card the head is drawn once: the card's pill row already names the channel, and a subject that is the
// card's own title is not repeated.
import { Chip, ActionButton } from '../parts/index.js';
import { Fragment, useState } from 'react';
import { dayMonthTime } from '../day.js';
import { kindWord } from '../records/shared.js';
import { attachmentUrl, type ThreadMessageView } from './api.js';
import './message-view.css';

/** The lines drawn before "Read all of it". */
export const OPEN_LINES = 4;

const WORDS: Record<'quoted' | 'signature' | 'footer', string> = {
  quoted: 'Show the earlier mails',
  signature: 'Show the signature and footer',
  footer: 'Show the signature and footer',
};

function names(list: Array<{ name: string; address: string | null }>): string {
  return list.map((p) => p.name || p.address || '').filter(Boolean).join(', ');
}

/** A subject as a title reads it: "Re:", "Fw:" and the like taken off, case and spacing ignored. */
function bare(subject: string): string {
  return subject.replace(/^\s*((re|fw|fwd|aw|wg|sv|tr)\s*:\s*)+/i, '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** The head of a message on a card, as one line: who to whom, when, the subject unless it is the card's title, the files. */
export function Head({ message, title }: { message: ThreadMessageView; title?: string | undefined }) {
  const to = names(message.to);
  const subject = message.subject && !(title && bare(message.subject) === bare(title)) ? message.subject : null;
  const n = message.attachments?.length ?? 0;
  const who = [message.from, to ? `to ${to}` : null].filter(Boolean).join(' ');
  const parts = [
    who ? <span key="who" title={message.fromAddress ?? undefined}>{who}</span> : null,
    <span key="when" className="when">{dayMonthTime(new Date(message.occurredAt))}</span>,
    subject ? <span key="subject" className="mv-subject">{subject}</span> : null,
    n ? <span key="files">{n} attachment{n === 1 ? '' : 's'}</span> : null,
  ].filter(Boolean);
  return <>{parts.map((x, i) => <Fragment key={i}>{i ? ' · ' : ''}{x}</Fragment>)}</>;
}

export function MessageView({ message, named = false, channel, showSubject = true, store, fold = false, open: openNow = false, title }: { message: ThreadMessageView; named?: boolean; channel?: string; showSubject?: boolean; store?: string; fold?: boolean; open?: boolean; title?: string }) {
  const [all, setAll] = useState(false);
  const [more, setMore] = useState(false);
  const [opened, setOpened] = useState(false);
  if (fold && !opened && !openNow) {
    return (
      <div className={named ? 'ev named message-view mv-folded-head' : 'ev message-view mv-folded-head'} aria-label="the message">
        <ActionButton reading type="button" className="hx-link m mv-head" aria-expanded={false} onClick={() => setOpened(true)}>
          <Head message={message} title={title} />
        </ActionButton>
      </div>
    );
  }
  if (fold) return <OpenOnCard message={message} store={store} title={title} onFold={openNow ? undefined : () => setOpened(false)} />;
  const lines = message.shown.split('\n');
  const long = lines.length > OPEN_LINES;
  const shownText = all || !long ? message.shown : lines.slice(0, OPEN_LINES).join('\n');
  const folded = message.folded.length > 0;
  const to = names(message.to);
  const cc = names(message.cc);

  return (
    <div className={named ? 'ev named message-view' : 'ev message-view'} aria-label="the message">
      <div className="meta">
        <Chip>{kindWord(channel ?? message.kind)}</Chip>
        {message.from ? <span title={message.fromAddress ?? undefined}>{message.from}</span> : null}
        <span className="when">{dayMonthTime(new Date(message.occurredAt))}</span>
      </div>
      {to ? <p className="note mv-people">To {message.to.map((p, i) => <span key={i} title={p.address ?? undefined}>{i ? ', ' : ''}{p.name || p.address}</span>)}</p> : null}
      {cc ? <p className="note mv-people">Cc {message.cc.map((p, i) => <span key={i} title={p.address ?? undefined}>{i ? ', ' : ''}{p.name || p.address}</span>)}</p> : null}
      {showSubject && message.subject ? <p className="note mv-subject">{message.subject}</p> : null}
      <div className="quote body mv-body">{shownText}</div>
      {message.attachments?.map((a) => <div key={a.id}>{store ? <a className="hx-link m" href={attachmentUrl(store, a.id)} target="_blank" rel="noopener noreferrer">{a.name}</a> : <span>{a.name}</span>}</div>)}
      {long && !all ? <ActionButton reading type="button" className="hx-link m" onClick={() => setAll(true)}>Read all of it</ActionButton> : null}
      {folded
        ? (
          <div className="mv-folded">
            <ActionButton reading type="button" className="hx-link m" onClick={() => setMore((v) => !v)} aria-expanded={more}>
              {more ? 'Hide what was folded away' : [...new Set(message.folded.map((f) => WORDS[f.kind]))].join(' · ')}
            </ActionButton>
            {more ? message.folded.map((f, i) => (
              <div key={i} className={`quote body mv-${f.kind}`}>{f.text}</div>
            )) : null}
          </div>
        )
        : null}
    </div>
  );
}

/** BUGS.md T73: the message on a card once pressed: its head once, then the whole cleaned text; the folded pieces stay a press away. */
function OpenOnCard({ message, store, title, onFold }: { message: ThreadMessageView; store?: string | undefined; title?: string | undefined; onFold?: (() => void) | undefined }) {
  const [more, setMore] = useState(false);
  const folded = message.folded.length > 0;
  return (
    <div className="ev message-view" aria-label="the message">
      {onFold
        ? <ActionButton reading type="button" className="hx-link m mv-head" aria-expanded={true} onClick={onFold}><Head message={message} title={title} /></ActionButton>
        : <p className="note mv-head"><Head message={message} title={title} /></p>}
      <div className="quote body mv-body">{message.shown}</div>
      {message.attachments?.map((a) => <div key={a.id}>{store ? <a className="hx-link m" href={attachmentUrl(store, a.id)} target="_blank" rel="noopener noreferrer">{a.name}</a> : <span>{a.name}</span>}</div>)}
      {folded
        ? (
          <div className="mv-folded">
            <ActionButton reading type="button" className="hx-link m" onClick={() => setMore((v) => !v)} aria-expanded={more}>
              {more ? 'Hide what was folded away' : [...new Set(message.folded.map((f) => WORDS[f.kind]))].join(' · ')}
            </ActionButton>
            {more ? message.folded.map((f, i) => (
              <div key={i} className={`quote body mv-${f.kind}`}>{f.text}</div>
            )) : null}
          </div>
        )
        : null}
    </div>
  );
}
